"use client";

import { useState, useRef, useEffect, FormEvent } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import AppLogo from "@/components/ui/AppLogo";

interface SiteHeaderProps {
  requestsUsed?: number;
  rateLimit?: number;
}

const LANGUAGES = [
  { label: "PHP", href: "/languages/php" },
  { label: "JavaScript", href: "/languages/javascript" },
  { label: "Python", href: "/languages/python" },
  { label: "Java", href: "/languages/java" },
  { label: "SQL", href: "/languages/sql" },
];

const ERROR_TYPES_LINK = { label: "Error Types", href: "/error-types" };

export default function SiteHeader({ requestsUsed, rateLimit }: SiteHeaderProps) {
  const hasRateLimit = typeof requestsUsed === 'number' && typeof rateLimit === 'number' && rateLimit > 0;
  const remaining = hasRateLimit ? (rateLimit - requestsUsed) : 0;
  const pct = hasRateLimit ? (requestsUsed / rateLimit) * 100 : 0;
  const isLow = remaining <= 3;
  const isExhausted = hasRateLimit ? remaining === 0 : false;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Global keyboard shortcut: Cmd/Ctrl+K
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
      if (e.key === "Escape") {
        setSearchOpen(false);
        setMobileOpen(false);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  function handleSearchSubmit(e: FormEvent) {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  }

  return (
    <>
      <header className="sticky top-0 z-50 bg-zinc-950/90 backdrop-blur-sm border-b border-zinc-800/60">
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 h-14 flex items-center justify-between gap-4">
          {/* Logo */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <AppLogo size={28} />
            <Link href="/error-explainer-tool">
              <span
                className="font-semibold text-white text-base tracking-tight hidden sm:block"
                style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
              >
                DevError<span className="text-cyan-400">Explainer</span>
              </span>
            </Link>
            <span className="hidden md:inline-flex items-center bg-zinc-800 text-zinc-400 text-xs px-2 py-0.5 rounded-full border border-zinc-700 ml-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              v1.0
            </span>
          </div>

          {/* Global Search Bar */}
          <div className="flex-1 max-w-md hidden sm:block">
            <button
              onClick={() => setSearchOpen(true)}
              className="w-full flex items-center gap-2.5 bg-zinc-900 border border-zinc-700/60 rounded-lg px-3 py-2 text-zinc-500 hover:border-zinc-600 hover:text-zinc-400 transition-all text-sm"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span className="flex-1 text-left text-xs">Search errors…</span>
              <kbd className="hidden lg:inline-flex items-center gap-1 text-xs bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-500">
                <span>⌘K</span>
              </kbd>
            </button>
          </div>

          {/* Nav Links */}
          <nav className="hidden md:flex items-center gap-1 flex-shrink-0">
            {/* Browse Errors Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen((v) => !v)}
                className="flex items-center gap-1.5 text-zinc-400 hover:text-zinc-200 text-sm px-3 py-1.5 rounded-lg hover:bg-zinc-800/60 transition-all duration-150"
              >
                Browse Errors
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  className={`transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-52 bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl overflow-hidden z-50">
                  {LANGUAGES.map((lang) => (
                    <Link
                      key={lang.href}
                      href={lang.href}
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/60" />
                      {lang.label}
                    </Link>
                  ))}
                  <div className="border-t border-zinc-800 mt-1 pt-1">
                    <Link
                      href={ERROR_TYPES_LINK.href}
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-400/60" />
                      {ERROR_TYPES_LINK.label}
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Blog Link */}
            <Link
              href="/blog"
              className="flex items-center gap-1.5 text-zinc-400 hover:text-zinc-200 text-sm px-3 py-1.5 rounded-lg hover:bg-zinc-800/60 transition-all duration-150"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
              Blog
            </Link>

            {/* Debug Link */}
            <Link
              href="/debug"
              className="flex items-center gap-1.5 text-zinc-400 hover:text-cyan-300 text-sm px-3 py-1.5 rounded-lg hover:bg-cyan-500/10 transition-all duration-150"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              Debug
            </Link>

          </nav>

          {/* Right side: mobile search + rate limit */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Mobile search button */}
            <button
              onClick={() => setSearchOpen(true)}
              className="sm:hidden text-zinc-400 hover:text-zinc-200 transition-colors p-1.5 rounded-lg hover:bg-zinc-800/60"
              aria-label="Search"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>

            {hasRateLimit && (
              <>
                {/* Rate Limit Indicator */}
                <div className="flex flex-col items-end gap-0.5">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs font-medium ${isExhausted ? "text-red-400" : isLow ? "text-amber-400" : "text-zinc-400"}`}>
                      {isExhausted ? "Limit reached" : `${remaining} left`}
                    </span>
                    {isLow && !isExhausted && (
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                    )}
                    {isExhausted && (
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                    )}
                  </div>
                  <div className="w-20 h-1 bg-zinc-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        isExhausted ? "bg-red-500" : isLow ? "bg-amber-500" : "bg-cyan-500"
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>

                <div className="w-px h-6 bg-zinc-800" />
              </>
            )}

            <a
              href="https://github.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex text-zinc-400 hover:text-zinc-200 transition-colors p-1.5 rounded-lg hover:bg-zinc-800/60"
              aria-label="View on GitHub"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
              </svg>
            </a>

            {/* Mobile hamburger placed at the far right (replaces GitHub position on small screens) */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="sm:hidden text-zinc-400 hover:text-zinc-200 transition-colors p-1.5 rounded-lg hover:bg-zinc-800/60"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18M3 12h18M3 18h18" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Search Modal Overlay */}
      {searchOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center pt-20 px-4"
          onClick={(e) => { if (e.target === e.currentTarget) setSearchOpen(false); }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm" />

          {/* Search Panel */}
          <div className="relative w-full max-w-xl bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden">
            <button
              onClick={() => setSearchOpen(false)}
              aria-label="Close search"
              className="absolute top-3 right-3 z-20 text-zinc-400 hover:text-zinc-200 p-1.5 rounded-lg"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <form onSubmit={handleSearchSubmit}>
              <div className="flex items-center gap-3 px-4 py-3.5 border-b border-zinc-800">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-400 flex-shrink-0">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder='Search errors… "undefined index", "typeerror", "indexerror"'
                  className="flex-1 bg-transparent text-zinc-100 placeholder-zinc-500 text-sm focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setSearchOpen(false)}
                  className="text-zinc-500 hover:text-zinc-300 transition-colors text-xs px-2 py-1 rounded border border-zinc-700 hover:border-zinc-600"
                >
                  ESC
                </button>
              </div>
            </form>

            {/* Quick suggestions */}
            <div className="p-3">
              <p className="text-xs text-zinc-600 mb-2 px-1">Popular searches</p>
              <div className="flex flex-wrap gap-2">
                {['undefined index', 'typeerror', 'python indexerror', 'null pointer', 'syntax error', 'sql syntax', 'nullpointerexception', 'segmentation fault'].map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      router.push(`/search?q=${encodeURIComponent(s)}`);
                      setSearchOpen(false);
                      setSearchQuery("");
                    }}
                    className="text-xs px-3 py-1.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200 transition-all"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Browse by language */}
            <div className="px-3 pb-3 border-t border-zinc-800 pt-3">
              <p className="text-xs text-zinc-600 mb-2 px-1">Browse by language</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { lang: 'php', color: 'text-violet-400 border-violet-400/30 bg-violet-400/5' },
                  { lang: 'javascript', color: 'text-yellow-400 border-yellow-400/30 bg-yellow-400/5' },
                  { lang: 'python', color: 'text-blue-400 border-blue-400/30 bg-blue-400/5' },
                  { lang: 'java', color: 'text-orange-400 border-orange-400/30 bg-orange-400/5' },
                  { lang: 'sql', color: 'text-emerald-400 border-emerald-400/30 bg-emerald-400/5' },
                ].map(({ lang, color }) => (
                  <Link
                    key={lang}
                    href={`/languages/${lang}`}
                    onClick={() => setSearchOpen(false)}
                    className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all hover:opacity-80 ${color}`}
                  >
                    {lang.toUpperCase()}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[90] sm:hidden"
          onClick={(e) => { if (e.target === e.currentTarget) setMobileOpen(false); }}
        >
          <div className="absolute inset-0 bg-zinc-950/70 backdrop-blur-sm" />

          <div ref={mobileRef} className="relative top-14 bg-zinc-900 border-t border-zinc-800 w-full shadow-xl">
            <div className="p-4 space-y-3">
              <div>
                <div className="text-xs text-zinc-500 uppercase mb-1">Browse Errors</div>
                <div className="grid grid-cols-2 gap-2">
                  {LANGUAGES.map((lang) => (
                    <Link
                      key={lang.href}
                      href={lang.href}
                      onClick={() => setMobileOpen(false)}
                      className="block px-3 py-2 rounded-lg bg-zinc-800 text-sm text-zinc-300 hover:bg-zinc-800/60"
                    >
                      {lang.label}
                    </Link>
                  ))}
                  <Link
                    href={ERROR_TYPES_LINK.href}
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2 rounded-lg bg-zinc-800 text-sm text-zinc-300 hover:bg-zinc-800/60"
                  >
                    {ERROR_TYPES_LINK.label}
                  </Link>
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-3">
                <Link href="/blog" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg text-zinc-300 hover:bg-zinc-800/60">Blog</Link>
                <Link href="/debug" onClick={() => setMobileOpen(false)} className="block px-3 py-2 rounded-lg text-zinc-300 hover:bg-cyan-500/10">Debug</Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}